import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Grid,
    GridItem,
    VStack,
    Link,
    HStack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import Banner from './partials/banner'
import Video from './partials/video'
import Title from './partials/title'
import HeroButton from './partials/hero-button'
import PDComponent from '../../pd-component'
import CountDownTimer from '../../../countdown-timer'
import CountDownTimerVariant from '../../../countdown-timer/partials/countDownTimerVariant'
import {PD_HERO, PD_BANNER, PD_HERO_XX_VALUE_BADGE} from '../../../../constants'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getHeightMultiplier} from './media-height'
import {getTextAlignment} from '../../../../utils/common-utils'

export const Hero = ({
    eagerLoadImage = false,
    desktopImage,
    mobileImage,
    imageAlt,

    desktopVideo,
    mobileVideo,
    desktopVideoThumbnail,
    mobileVideoThumbnail,
    videoAlt,
    videoEnableSound,
    videoDisableAutoPlay,

    mediaSize = 'hero',
    desktopMediaPosition = 'full',
    mobileMediaPosition = 'full',
    desktopMediaBackground,
    mobileMediaBackground,
    desktopMediaOverlay,
    mobileMediaOverlay,

    desktopHeight = '100%',
    mobileHeight = '100%',
    disableAutoCrop = false,

    eyebrowType,
    eyebrowText,
    eyebrowTextSize,
    eyebrowTextSizeDesktop,
    eyebrowTextColor = 'white',
    eyebrowTextColorDesktop = 'white',
    eyebrowFontFamily,
    eyebrowPosition = 'middle-center',
    eyebrowPositionDesktop = 'middle-center',
    eyebrowtag,

    titleType,
    title,
    titleFontFamily,
    titleTag,
    titleColor = 'white',
    titleColorDesktop = 'white',
    titleSizeDesktop = 'normal',
    titleSizeMobile = 'normal',

    subtitle,
    subtitleTextSize,
    subtitleTextSizeDesktop,
    subtitleColor = 'white',
    subtitleColorDesktop = 'white',
    subtitleFontFamily,
    addTitleFreeText,

    ctaTypeMobile = 'button',
    ctaTypeDesktop = 'button',
    ctaSizeMobile = 'normal',
    ctaSizeDesktop = 'normal',

    buttonText1,
    buttonColor1,
    buttonTextColor1,
    buttonTextColorDesktop1,
    buttonBgColor1,
    buttonTextColor2,
    buttonTextColorDesktop2,
    buttonBgColor2,
    buttonLink1,
    buttonFontFamily,
    dropdownLinkLabel1,
    dropdownLinkUrl1,
    dropdownLinkLabel2,
    dropdownLinkUrl2,
    dropdownLinkLabel3,
    dropdownLinkUrl3,
    dropdownLinkLabel4,
    dropdownLinkUrl4,
    dropdownLinkLabel5,
    dropdownLinkUrl5,
    dropdownLinkLabel6,
    dropdownLinkUrl6,

    buttonText2,
    buttonColor2 = 'dark',
    buttonLink2,

    overlayLink,
    overlayLinkAriaLabel,

    hour,
    minute,
    second,
    datetimepicker = {},

    titlePosition = 'middle-center',
    titlePositionDesktop = 'middle-center',
    subtitlePosition = 'middle-center',
    subtitlePositionDesktop = 'middle-center',
    ctaPosition = 'middle-center',
    ctaPositionDesktop = 'middle-center',

    typeId = PD_HERO,
    name,
    regions
}) => {
    const appOrigin = useAppOrigin()
    const pdTagManager = usePdTagManager()
    useEffect(() => {
        pdTagManager.viewComponent(typeId, name)
    }, [desktopImage])
    const variant = typeId === PD_BANNER ? 'banner' : 'hero'
    const shouldDisplayVideo = (desktopVideo || mobileVideo) && !(desktopImage || mobileImage)
    const hasMedia = shouldDisplayVideo || desktopImage || mobileImage
    const hasButton1 = buttonText1 && buttonLink1
    const hasButton2 = buttonText2 && buttonLink2
    const hasHeading = title || hasButton1 || hasButton2

    const getTextTransform = (fontFamily) => {
        if (fontFamily === 'brandon-grotesque') {
            return 'uppercase'
        } else if (fontFamily === 'caslon-italic') {
            return 'lowercase'
        } else {
            return 'none'
        }
    }

    const eyebrowTextTransform = getTextTransform(eyebrowFontFamily)
    const subtitleTextTransform = getTextTransform(subtitleFontFamily)

    const promotionComponents =
        regions?.filter(
            (region) => region?.id === 'heroPromotions' && region?.components?.length > 0
        )?.[0]?.components || []
    const calloutsComponents =
        regions?.filter(
            (region) => region?.id === 'customFlagsCallouts' && region?.components?.length > 0
        )?.[0]?.components || []
    const hasXXValueBadge = calloutsComponents.some(
        (component) => component?.typeId === PD_HERO_XX_VALUE_BADGE
    )
    const mainAreaPadding =
        calloutsComponents.length > 0
            ? hasXXValueBadge
                ? {paddingY: {base: '110px', md: '30px', xl: '90px'}}
                : {paddingY: {base: '70px', md: '30px', xl: '90px'}}
            : {paddingY: {base: '40px', md: '30px', xl: '90px'}}

    const dropdownLinks1 = [
        dropdownLinkLabel1 &&
            dropdownLinkUrl1 && {label: dropdownLinkLabel1, url: dropdownLinkUrl1},
        dropdownLinkLabel2 &&
            dropdownLinkUrl2 && {label: dropdownLinkLabel2, url: dropdownLinkUrl2},
        dropdownLinkLabel3 &&
            dropdownLinkUrl3 && {label: dropdownLinkLabel3, url: dropdownLinkUrl3},
        dropdownLinkLabel4 &&
            dropdownLinkUrl4 && {label: dropdownLinkLabel4, url: dropdownLinkUrl4},
        dropdownLinkLabel5 &&
            dropdownLinkUrl5 && {label: dropdownLinkLabel5, url: dropdownLinkUrl5},
        dropdownLinkLabel6 && dropdownLinkUrl6 && {label: dropdownLinkLabel6, url: dropdownLinkUrl6}
    ].filter(Boolean)

    const hasBanner = promotionComponents?.length || hasXXValueBadge

    // Define the 3x3 grid map for positions
    const gridAreas = [
        'top-left',
        'top-center',
        'top-right',
        'middle-left',
        'middle-center',
        'middle-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
        'left-half',
        'right-half'
    ]

    const fontSizeMap = {
        normal: '32px',
        small: '24px',
        medium: '36px',
        large: '48px'
    }

    const getMobileArea = (position) => {
        if (position) {
            if (position.startsWith('top')) return 'top'
            if (position.startsWith('middle')) return 'middle'
            if (position.startsWith('bottom')) return 'bottom'
        }

        return 'middle'
    }

    const positionGroups = {
        mobile: Object.fromEntries(gridAreas.map((pos) => [pos, []])),
        desktop: Object.fromEntries(gridAreas.map((pos) => [pos, []]))
    }

    const getPosition = (position) => {
        if (position === 'left-half') return 'middle-left'
        if (position === 'right-half') return 'middle-right'

        return position
    }

    // date and time object
    const targetDateConfig = {
        dateVal: datetimepicker?.value,
        hour,
        minute,
        second
    }

    const heroTimerStyles = {...CountDownTimerVariant['hero']}

    heroTimerStyles.headline = {
        ...heroTimerStyles.headline,
        fontFamily: 'caslon',
        color: {base: titleColor, lg: titleColorDesktop}
    }

    heroTimerStyles.eyeBrow = {
        ...heroTimerStyles.eyeBrow,
        fontFamily: eyebrowFontFamily,
        color: {base: eyebrowTextColor, lg: eyebrowTextColorDesktop},
        textTransform: eyebrowTextTransform,
        fontStyle: eyebrowFontFamily === 'caslon-italic' ? 'italic' : 'normal'
    }

    // extend the styles base on configuration
    heroTimerStyles.box = {
        ...heroTimerStyles.box,
        fontFamily: eyebrowFontFamily,
        textTransform: eyebrowTextTransform,
        fontStyle: eyebrowFontFamily === 'caslon-italic' ? 'italic' : 'normal'
    }

    // Push components to their respective positions
    // Render Eyebrow as text or timer
    if (
        (datetimepicker?.value && eyebrowType === 'timer') ||
        (eyebrowText && eyebrowType === 'text')
    ) {
        const textAlignEyebrowMobile = getTextAlignment(eyebrowPosition)
        const textAlignEyebrowDesktop = getTextAlignment(eyebrowPositionDesktop)
        const eyebrowComponent = (
            <>
                {eyebrowType === 'text' && (
                    <Text
                        key="eyebrowText"
                        className="hero-eyebrow-text"
                        fontSize={{
                            base: fontSizeMap[eyebrowTextSize] ?? '24px',
                            lg: fontSizeMap[eyebrowTextSizeDesktop] ?? '24px'
                        }}
                        color={{base: eyebrowTextColor, lg: eyebrowTextColorDesktop}}
                        fontFamily={eyebrowFontFamily}
                        textAlign={{
                            base: textAlignEyebrowMobile,
                            lg: textAlignEyebrowDesktop
                        }}
                        textTransform={eyebrowTextTransform}
                        fontStyle={eyebrowFontFamily === 'caslon-italic' ? 'italic' : 'normal'}
                    >
                        {eyebrowText}
                    </Text>
                )}
                {eyebrowType === 'timer' && (
                    <Box
                        className="hero-eyebrow-timer"
                        textAlign="center"
                        color={{base: eyebrowTextColor, lg: eyebrowTextColorDesktop}}
                        {...CountDownTimerVariant['hero'].box}
                    >
                        <CountDownTimer
                            tag={eyebrowtag}
                            targetDate={targetDateConfig}
                            variantStyles={heroTimerStyles}
                            type="eyebrow"
                        />
                    </Box>
                )}
            </>
        )
        positionGroups.mobile[eyebrowPosition]?.push(eyebrowComponent)
        positionGroups.desktop[getPosition(eyebrowPositionDesktop)]?.push(eyebrowComponent)
    }

    // Render timer as Headline Title
    if (datetimepicker?.value && titleType === 'timer') {
        const titleTimerComponent = (
            <>
                <Box
                    className="hero-title-timer"
                    textAlign="center"
                    color={{base: titleColor, lg: titleColorDesktop}}
                    {...CountDownTimerVariant['hero'].box}
                >
                    <CountDownTimer
                        tag={titleTag}
                        targetDate={targetDateConfig}
                        variantStyles={heroTimerStyles}
                        type="default"
                    />
                </Box>
            </>
        )
        positionGroups.mobile[titlePosition]?.push(titleTimerComponent)
        positionGroups.desktop[getPosition(titlePositionDesktop)]?.push(titleTimerComponent)
    }

    if (hasHeading && titleType !== 'timer') {
        const titleComponent = (
            <Title
                key="title"
                className="hero-title"
                title={title}
                tag={titleTag}
                color={titleColor}
                desktopColor={titleColorDesktop}
                titleSizeMobile={titleSizeMobile}
                titleSizeDesktop={titleSizeDesktop}
                titleFontFamily={titleFontFamily}
                addTitleFreeText={addTitleFreeText}
                titlePositionDesktop={titlePositionDesktop}
                titlePosition={titlePosition}
            />
        )
        positionGroups.mobile[titlePosition]?.push(titleComponent)
        positionGroups.desktop[getPosition(titlePositionDesktop)]?.push(titleComponent)
    }

    if (subtitle) {
        const subtitleFontWeightMap = {
            caslon: '400',
            cormorant: '500',
            inter: '300'
        }
        const textAlignSubtitleMobile = getTextAlignment(subtitlePosition)
        const textAlignSubtitleDesktop = getTextAlignment(subtitlePositionDesktop)
        const subTitleComponent = (
            <Text
                key="subtitle"
                className="hero-subtitle"
                fontSize={{
                    base: fontSizeMap[subtitleTextSize] ?? '24px',
                    lg: fontSizeMap[subtitleTextSizeDesktop] ?? '24px'
                }}
                color={{base: subtitleColor, lg: subtitleColorDesktop}}
                fontFamily={subtitleFontFamily}
                textAlign={{
                    base: textAlignSubtitleMobile,
                    lg: textAlignSubtitleDesktop
                }}
                fontWeight={subtitleFontWeightMap[subtitleFontFamily]}
                textTransform={subtitleTextTransform}
                fontStyle={subtitleFontFamily === 'caslon-italic' ? 'italic' : 'normal'}
            >
                {subtitle}
            </Text>
        )
        positionGroups.mobile[subtitlePosition]?.push(subTitleComponent)
        positionGroups.desktop[getPosition(subtitlePositionDesktop)]?.push(subTitleComponent)
    }

    ctaPositionDesktop

    if (hasButton1 || hasButton2) {
        const buttonsComponent = (
            <HStack
                key="cta"
                className="hero-cta-group"
                gap={{base: '20px', md: '40px'}}
                mt="30px"
                flexWrap={{base: 'wrap', md: 'nowrap'}}
                justifyContent="center"
                alignSelf={{
                    base: ctaPosition,
                    lg: ctaPositionDesktop.endsWith('half') ? 'center' : 'unset'
                }}
            >
                {hasButton1 && (
                    <HeroButton
                        className="hero-cta-button hero-cta-button-1"
                        text={buttonText1}
                        url={buttonLink1}
                        color={buttonColor1} // Text color fallback
                        bgColor={buttonBgColor1} // Legacy fallback
                        buttonBgColor={buttonBgColor1 ? buttonBgColor1 : ''}
                        buttonTextColor={buttonTextColor1}
                        buttonTextColorDesktop={buttonTextColorDesktop1}
                        ctaSizeMobile={ctaSizeMobile}
                        ctaSizeDesktop={ctaSizeDesktop}
                        ctaTypeMobile={ctaTypeMobile}
                        ctaTypeDesktop={ctaTypeDesktop}
                        fontFamily={buttonFontFamily}
                        subtitle={subtitle}
                        index={0}
                        imageUrl={shouldDisplayVideo ? '' : desktopImage || mobileImage}
                        videoUrl={shouldDisplayVideo ? desktopVideo : ''}
                        dropdownLinks={dropdownLinks1}
                        w="auto"
                    />
                )}
                {hasButton2 && (
                    <HeroButton
                        className="hero-cta-button hero-cta-button-2"
                        text={buttonText2}
                        url={buttonLink2}
                        color={buttonColor2}
                        bgColor={buttonBgColor2}
                        buttonBgColor={buttonBgColor2 ? buttonBgColor2 : ''}
                        buttonTextColor={buttonTextColor2}
                        buttonTextColorDesktop={buttonTextColorDesktop2}
                        ctaSizeMobile={ctaSizeMobile}
                        ctaSizeDesktop={ctaSizeDesktop}
                        ctaTypeMobile={ctaTypeMobile}
                        ctaTypeDesktop={ctaTypeDesktop}
                        subtitle={subtitle}
                        index={1}
                        imageUrl={shouldDisplayVideo ? '' : desktopImage || mobileImage}
                        videoUrl={shouldDisplayVideo ? desktopVideo : ''}
                        fontFamily={buttonFontFamily}
                        w="auto"
                    />
                )}
            </HStack>
        )
        positionGroups.mobile[ctaPosition]?.push(buttonsComponent)
        positionGroups.desktop[getPosition(ctaPositionDesktop)]?.push(buttonsComponent)
    }

    // Group components for mobile: { top: [], middle: [], bottom: [] }
    const mobileGroups = {top: [], middle: [], bottom: []}

    Object.entries(positionGroups.mobile).forEach(([position, components]) => {
        if (!components.length || !position) return

        const area = getMobileArea(position)
        const mappedComponents = components.map((component) => {
            const mobilePosition = position
            const desktopPosition =
                Object.entries(positionGroups.desktop).find(([, comps]) =>
                    comps.includes(component)
                )?.[0] || 'middle-center'

            const mobileAlignment = mobilePosition.endsWith('center')
                ? 'center'
                : mobilePosition.endsWith('right')
                ? 'flex-end'
                : 'flex-start'

            const desktopAlignment = desktopPosition.endsWith('center')
                ? 'center'
                : desktopPosition.endsWith('right')
                ? 'flex-end'
                : 'flex-start'

            return {
                component,

                alignment: {
                    base: mobileAlignment,
                    md: desktopAlignment
                }
            }
        })

        if (mappedComponents.length) {
            mobileGroups[area] = (mobileGroups[area] || []).concat(mappedComponents)
        }
    })

    const route = overlayLink?.split('.com')?.[1]
    const isExternalDomain = overlayLink?.indexOf('http') !== -1
    const linkProps = isExternalDomain
        ? {href: overlayLink}
        : {as: RouteLink, to: route || overlayLink}
    const desktopHeightMultiplier = getHeightMultiplier(desktopHeight)
    const mobileHeightMultiplier = getHeightMultiplier(mobileHeight)
    // Render final layout
    return (
        <Box className="hero" marginX="0" position="relative">
            <Grid
                className="hero-grid"
                background={{base: mobileMediaBackground, md: desktopMediaBackground}}
                position="relative"
                width="100%"
                color="white"
                aspectRatio={{
                    base: '414 / 654',
                    md:
                        variant === 'hero'
                            ? mediaSize === 'hero'
                                ? '1920 / 900'
                                : '1920 / 480'
                            : '1920 / 684'
                }}
                templateColumns={{
                    base: '1fr',
                    md: desktopMediaPosition === 'full' ? '1fr' : hasMedia ? '1fr 1fr' : '1fr'
                }}
                templateRows={{
                    base: mobileMediaPosition === 'full' ? '1fr' : hasMedia ? 'auto 1fr' : '1fr',
                    md: '1fr'
                }}
                templateAreas={{
                    base:
                        mobileMediaPosition === 'full'
                            ? `"full"`
                            : hasMedia
                            ? `
                                "top"
                                "bottom"
                            `
                            : `"full"`,
                    md:
                        desktopMediaPosition === 'full'
                            ? `"full"`
                            : hasMedia
                            ? `"left right"`
                            : `"full"`
                }}
                height={{
                    base: `calc(100vw * (654 / 414) * ${mobileHeightMultiplier})`,
                    md:
                        variant === 'hero'
                            ? mediaSize === 'hero'
                                ? `calc(100vw * (900 / 1920) * ${desktopHeightMultiplier})`
                                : `calc(100vw * (480 / 1920) * ${desktopHeightMultiplier})`
                            : `calc(100vw * (684 / 1920) * ${desktopHeightMultiplier})`
                }}
            >
                {/* Background media */}
                {hasMedia && (
                    <Box
                        className="hero-media"
                        height="100%"
                        gridArea={{base: mobileMediaPosition, md: desktopMediaPosition}}
                        position="relative"
                        onClick={() => {
                            pdTagManager.selectComponent({
                                componentId: typeId,
                                componentName: name,
                                titleName: title,
                                linkUrl:
                                    overlayLink?.indexOf('http') !== -1
                                        ? overlayLink
                                        : `${appOrigin}${overlayLink}`,
                                imageUrl: shouldDisplayVideo ? '' : desktopImage || mobileImage,
                                subText: subtitle,
                                videoUrl: shouldDisplayVideo ? desktopVideo : '',
                                tileHeading: title
                            })
                        }}
                    >
                        {shouldDisplayVideo ? (
                            <Video
                                className="hero-media-video"
                                desktopVideo={desktopVideo}
                                mobileVideo={mobileVideo || desktopVideo}
                                desktopVideoThumbnail={desktopVideoThumbnail}
                                mobileVideoThumbnail={mobileVideoThumbnail}
                                videoDisableAutoPlay={videoDisableAutoPlay}
                                videoEnableSound={videoEnableSound}
                                videoAlt={videoAlt}
                                variant={variant}
                                mediaSize={mediaSize}
                                desktopMediaPosition={desktopMediaPosition}
                                mobileMediaPosition={mobileMediaPosition}
                            />
                        ) : desktopImage || mobileImage ? (
                            <Banner
                                className="hero-media-banner"
                                desktopImage={desktopImage}
                                mobileImage={mobileImage}
                                imageAlt={imageAlt}
                                variant={variant}
                                mediaSize={mediaSize}
                                desktopMediaPosition={desktopMediaPosition}
                                mobileMediaPosition={mobileMediaPosition}
                                desktopHeight={desktopHeight}
                                mobileHeight={mobileHeight}
                                disableAutoCrop={disableAutoCrop}
                                eagerLoadImage={eagerLoadImage}
                            />
                        ) : null}

                        {desktopMediaOverlay && (
                            <Box
                                className="hero-media-overlay hero-media-overlay-desktop"
                                position="absolute"
                                top="0"
                                left="0"
                                right="0"
                                bottom="0"
                                background={desktopMediaOverlay}
                            />
                        )}

                        {mobileMediaOverlay && (
                            <Box
                                className="hero-media-overlay hero-media-overlay-mobile"
                                position="absolute"
                                top="0"
                                left="0"
                                right="0"
                                bottom="0"
                                background={mobileMediaOverlay}
                            />
                        )}
                    </Box>
                )}

                {/* Overlay content */}
                <Grid
                    className="hero-content-grid"
                    {...mainAreaPadding}
                    position="relative"
                    gridArea={{
                        base:
                            mobileMediaPosition === 'top' && hasMedia
                                ? 'bottom'
                                : mobileMediaPosition === 'bottom' && hasMedia
                                ? 'top'
                                : 'full',
                        md:
                            desktopMediaPosition === 'left' && hasMedia
                                ? 'right'
                                : desktopMediaPosition === 'right' && hasMedia
                                ? 'left'
                                : 'full'
                    }}
                    width="100%"
                    height="100%"
                    px={{
                        base: '10px',
                        md: '30px',
                        xl:
                            desktopMediaPosition === 'left' || desktopMediaPosition === 'right'
                                ? '20px'
                                : '80px'
                    }}
                    templateRows={{
                        base: 'repeat(3, auto)',
                        md: promotionComponents?.length
                            ? 'repeat(3, auto)'
                            : 'repeat(3, minmax(33%, auto))'
                    }}
                    templateColumns={{
                        base: '1fr',
                        md: 'repeat(3, 1fr)',
                        xl:
                            desktopMediaPosition === 'left' || desktopMediaPosition === 'right'
                                ? 'repeat(3, auto)'
                                : promotionComponents?.length
                                ? 'repeat(3, auto)'
                                : 'repeat(3, minmax(33%, 50%))'
                    }}
                    templateAreas={{
                        base: `
                            "top"
                            "middle"
                            "bottom"
                        `,
                        md: `
                            "top-left top-center top-right"
                            "middle-left middle-center middle-right"
                            "bottom-left bottom-center bottom-right"
                        `
                    }}
                >
                    {promotionComponents.map((component, index) => (
                        <PDComponent
                            key={index}
                            className="hero-promotion-component"
                            component={component}
                            variant={
                                desktopMediaPosition === 'left' || desktopMediaPosition === 'right'
                                    ? `${mediaSize}Side`
                                    : mediaSize
                            }
                        />
                    ))}

                    {/* Mobile layout */}
                    {['top', 'middle', 'bottom'].map((area) => (
                        <GridItem
                            key={area}
                            className={`hero-mobile-area hero-mobile-area-${area}`}
                            area={{base: area, md: undefined}}
                            display={{base: 'flex', md: 'none'}}
                            flexDirection="column"
                            justifyContent={
                                area === 'top'
                                    ? 'flex-start'
                                    : area === 'middle'
                                    ? 'center'
                                    : 'flex-end'
                            }
                            marginTop={area === 'top' && hasBanner ? '40px' : '0'}
                        >
                            {(mobileGroups[area] || []).map(({component, alignment}, idx) => (
                                <Box
                                    key={idx}
                                    className="hero-mobile-item"
                                    display="flex"
                                    justifyContent={alignment.base}
                                    width="100%"
                                >
                                    {component}
                                </Box>
                            ))}
                        </GridItem>
                    ))}
                    {Object.entries(positionGroups.desktop).map(([position, components]) => {
                        if (!components.length || !position) return null

                        const alignItems = position.startsWith('top')
                            ? 'flex-start'
                            : position.startsWith('middle')
                            ? 'center'
                            : 'flex-end'

                        const justifyContent = position.endsWith('left')
                            ? 'flex-start'
                            : position.endsWith('center')
                            ? 'center'
                            : 'flex-end'

                        return (
                            <GridItem
                                key={position}
                                className={`hero-desktop-area hero-desktop-area-${position}`}
                                area={{base: undefined, md: position}}
                                display={{base: 'none', md: 'flex'}}
                                justifyContent={justifyContent}
                                alignItems={alignItems}
                            >
                                <VStack
                                    className="hero-desktop-stack"
                                    spacing="10px"
                                    align={
                                        position.endsWith('center')
                                            ? 'center'
                                            : position.endsWith('right')
                                            ? 'flex-end'
                                            : 'flex-start'
                                    }
                                    width="100%"
                                    sx={{
                                        a: {
                                            textUnderlineOffset: '4px'
                                        }
                                    }}
                                    textAlign={
                                        position.endsWith('center')
                                            ? 'center'
                                            : position.endsWith('right')
                                            ? 'right'
                                            : 'left'
                                    }
                                >
                                    {components}
                                </VStack>
                            </GridItem>
                        )
                    })}
                </Grid>

                {calloutsComponents.map((component, index) => (
                    <PDComponent
                        key={index}
                        className="hero-callout-component"
                        component={component}
                        variant={
                            desktopMediaPosition === 'left' || desktopMediaPosition === 'right'
                                ? `${mediaSize}Side`
                                : mediaSize
                        }
                    />
                ))}

                {/* Full overlay link */}
                {overlayLink && (
                    <Link
                        className="hero-overlay-link"
                        {...linkProps}
                        onClick={() => {
                            pdTagManager.selectComponent({
                                componentId: typeId,
                                componentName: name,
                                titleName: title,
                                linkUrl:
                                    overlayLink?.indexOf('http') !== -1
                                        ? overlayLink
                                        : `${appOrigin}${overlayLink}`,
                                imageUrl: shouldDisplayVideo ? '' : desktopImage || mobileImage,
                                videoUrl: shouldDisplayVideo ? desktopVideo : '',
                                subText: subtitle,
                                tileHeading: title
                            })
                        }}
                        aria-label={overlayLinkAriaLabel || title}
                        position="absolute"
                        top="0"
                        left="0"
                        width="100%"
                        height="100%"
                    />
                )}
            </Grid>
        </Box>
    )
}

Hero.propTypes = {
    eagerLoadImage: PropTypes.bool,
    desktopImage: PropTypes.string,
    mobileImage: PropTypes.string,
    imageAlt: PropTypes.string,

    desktopVideo: PropTypes.string,
    mobileVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    videoAlt: PropTypes.string,
    videoEnableSound: PropTypes.bool,
    videoDisableAutoPlay: PropTypes.bool,

    mediaSize: PropTypes.string,
    desktopMediaPosition: PropTypes.string,
    mobileMediaPosition: PropTypes.string,
    desktopMediaBackground: PropTypes.string,
    mobileMediaBackground: PropTypes.string,
    desktopMediaOverlay: PropTypes.string,
    mobileMediaOverlay: PropTypes.string,

    desktopHeight: PropTypes.string,
    mobileHeight: PropTypes.string,
    disableAutoCrop: PropTypes.bool,

    buttonText1: PropTypes.string,
    buttonColor1: PropTypes.string,
    buttonTextColor1: PropTypes.string,
    buttonTextColorDesktop1: PropTypes.string,
    buttonTextColor2: PropTypes.string,
    buttonTextColorDesktop2: PropTypes.string,
    buttonBgColor1: PropTypes.string,
    buttonBgColor2: PropTypes.string,
    buttonLink1: PropTypes.string,
    buttonFontFamily: PropTypes.string,
    dropdownLinkLabel1: PropTypes.string,
    dropdownLinkUrl1: PropTypes.string,
    dropdownLinkLabel2: PropTypes.string,
    dropdownLinkUrl2: PropTypes.string,
    dropdownLinkLabel3: PropTypes.string,
    dropdownLinkUrl3: PropTypes.string,
    dropdownLinkLabel4: PropTypes.string,
    dropdownLinkUrl4: PropTypes.string,
    dropdownLinkLabel5: PropTypes.string,
    dropdownLinkUrl5: PropTypes.string,
    dropdownLinkLabel6: PropTypes.string,
    dropdownLinkUrl6: PropTypes.string,

    buttonText2: PropTypes.string,
    buttonColor2: PropTypes.string,
    buttonLink2: PropTypes.string,

    eyebrowType: PropTypes.string,
    eyebrowText: PropTypes.string,
    eyebrowTextSize: PropTypes.string,
    eyebrowTextSizeDesktop: PropTypes.string,
    eyebrowTextColor: PropTypes.string,
    eyebrowTextColorDesktop: PropTypes.string,
    eyebrowFontFamily: PropTypes.string,
    eyebrowPosition: PropTypes.string,
    eyebrowPositionDesktop: PropTypes.string,
    eyebrowtag: PropTypes.string,

    titleType: PropTypes.string,
    title: PropTypes.string,
    titleFontFamily: PropTypes.string,
    subtitle: PropTypes.string,
    subtitleTextSize: PropTypes.string,
    subtitleTextSizeDesktop: PropTypes.string,
    subtitleColor: PropTypes.string,
    subtitleColorDesktop: PropTypes.string,
    subtitleFontFamily: PropTypes.string,
    titleTag: PropTypes.string,
    titleColor: PropTypes.string,
    titleColorDesktop: PropTypes.string,
    headlinePosition: PropTypes.string,
    titleSizeDesktop: PropTypes.string,
    titleSizeMobile: PropTypes.string,
    addTitleFreeText: PropTypes.bool,

    overlayLink: PropTypes.string,
    overlayLinkAriaLabel: PropTypes.string,

    ctaTypeMobile: PropTypes.string,
    ctaTypeDesktop: PropTypes.string,
    ctaSizeMobile: PropTypes.string,
    ctaSizeDesktop: PropTypes.string,

    hour: PropTypes.string,
    minute: PropTypes.string,
    second: PropTypes.string,
    datetimepicker: PropTypes.object,

    typeId: PropTypes.string,
    name: PropTypes.any,

    titlePosition: PropTypes.string,
    titlePositionDesktop: PropTypes.string,
    subtitlePosition: PropTypes.string,
    subtitlePositionDesktop: PropTypes.string,
    ctaPosition: PropTypes.string,
    ctaPositionDesktop: PropTypes.string,

    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string,
                    typeId: PropTypes.string,
                    data: PropTypes.object
                })
            )
        })
    )
}

Hero.displayName = 'HeroBannerWithBox'

export default Hero
